import { useCallback, useRef } from 'react';

type SoundName = 'move' | 'win' | 'lose' | 'draw' | 'chat' | 'matchFound';

// Generate simple tones using Web Audio API — no external files needed
const audioCtxRef = { current: null as AudioContext | null };

function getAudioCtx(): AudioContext {
  if (!audioCtxRef.current) {
    audioCtxRef.current = new AudioContext();
  }
  return audioCtxRef.current;
}

function playTone(frequency: number, duration: number, type: OscillatorType = 'sine', volume = 0.15) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    gain.gain.value = volume;
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch {
    // Audio not supported
  }
}

const SOUNDS: Record<SoundName, () => void> = {
  move: () => playTone(600, 0.08, 'square', 0.08),
  win: () => {
    playTone(523, 0.15, 'sine', 0.12);
    setTimeout(() => playTone(659, 0.15, 'sine', 0.12), 150);
    setTimeout(() => playTone(784, 0.3, 'sine', 0.12), 300);
  },
  lose: () => {
    playTone(400, 0.2, 'sine', 0.1);
    setTimeout(() => playTone(300, 0.3, 'sine', 0.1), 200);
  },
  draw: () => {
    playTone(440, 0.15, 'sine', 0.1);
    setTimeout(() => playTone(440, 0.2, 'sine', 0.1), 200);
  },
  chat: () => playTone(800, 0.06, 'sine', 0.06),
  matchFound: () => {
    playTone(440, 0.1, 'sine', 0.1);
    setTimeout(() => playTone(660, 0.1, 'sine', 0.1), 100);
    setTimeout(() => playTone(880, 0.15, 'sine', 0.1), 200);
  },
};

export function useSound() {
  const enabled = useRef(true);

  const play = useCallback((name: SoundName) => {
    if (enabled.current) {
      SOUNDS[name]();
    }
  }, []);

  const toggle = useCallback(() => {
    enabled.current = !enabled.current;
    return enabled.current;
  }, []);

  return { play, toggle, enabled };
}
